import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";

const pages = {
  privacy: {
    title: "Privacy Policy",
    subtitle: "How Matka Kings collects, uses and protects user information.",
    updated: "11 May 2026",
    sections: [
      ["Information We Collect", "We may collect your name, mobile number, email address, device information, login activity, referral information, payment request details, screenshots submitted by you, and usage activity required to run your account."],
      ["How We Use Information", "We use information to create and secure accounts, process deposits and withdrawals, provide support, send OTP and account notifications, prevent fraud, maintain platform safety, and improve our services."],
      ["Payments", "Payment screenshots, UPI references, wallet entries and withdrawal details are used only for verification, accounting, dispute handling and compliance record keeping."],
      ["Notifications", "If enabled, we may send service notifications such as account alerts, result updates, payment status updates and support messages. You can manage notification permissions from your device settings."],
      ["Data Sharing", "We do not sell personal data. We may share limited data with service providers such as hosting, analytics, OTP, Firebase notification, payment verification and support tools where needed to operate the platform."],
      ["Data Security", "We use reasonable technical and administrative safeguards to protect user information. No online service can guarantee absolute security, so users should keep account access private."],
      ["User Choices", "You may request correction of account details, support help, or account-related assistance by contacting us through the Contact Us page."],
      ["Children", "Our platform is intended only for users aged 18 years or above. We do not knowingly allow minors to register or use paid features."],
      ["Contact", "For privacy questions, contact us at support@matkakings.com or through the WhatsApp support link on the website."]
    ]
  },
  terms: {
    title: "Terms & Conditions",
    subtitle: "Rules for using Matka Kings website and services.",
    updated: "11 May 2026",
    sections: [
      ["Acceptance", "By accessing or using Matka Kings, you agree to these Terms & Conditions and all rules shown inside the platform."],
      ["Eligibility", "You must be 18 years or older and legally allowed to use this type of service in your location. If this service is restricted in your area, you must not use it."],
      ["Account Responsibility", "Users are responsible for keeping their mobile number, OTP, password, device and account access secure. Activity from your account may be treated as your responsibility."],
      ["Game Rules", "Bets, timings, rates, results, limits and withdrawal rules shown on the website apply to all users. Bids placed after market closing may be rejected or invalid."],
      ["Wallet & Transactions", "Deposits and withdrawals are subject to verification, minimum limits, daily limits, bank/UPI correctness and admin approval where applicable."],
      ["Fair Use", "Fraud, multiple account abuse, fake payment proof, referral abuse, technical manipulation or misuse of the platform may lead to account blocking, wallet hold or cancellation of requests."],
      ["Service Availability", "We try to keep the service available, but maintenance, network issues, device problems, payment delays or third-party failures may affect access."],
      ["Changes", "We may update these terms, game rules, limits, rates or features from time to time. Continued use means you accept the updated terms."],
      ["Contact", "For support or disputes, contact support@matkakings.com or use the support option on the website."]
    ]
  },
  refund: {
    title: "Refund & Cancellation Policy",
    subtitle: "Deposit, withdrawal and cancellation rules.",
    updated: "11 May 2026",
    sections: [
      ["Deposits", "Deposit requests are credited after payment verification. Users must submit correct payment proof and amount details."],
      ["Failed or Duplicate Payments", "If a payment is deducted but not credited, users should contact support with UPI transaction ID, date, amount and screenshot. Verified duplicate or failed credits may be adjusted to the wallet or refunded where applicable."],
      ["Bets", "Once a valid bid is placed before market closing, it generally cannot be cancelled by the user. Invalid, duplicate, technical-error or admin-reverted bets may be corrected according to platform records."],
      ["Withdrawals", "Withdrawal requests are processed according to user limits, available balance, bank/UPI details and verification checks. Incorrect details may delay or reject a request."],
      ["Refund Time", "Approved refunds or corrections may take time depending on bank, UPI or payment provider processing."],
      ["Contact", "For payment help, contact support@matkakings.com with your registered mobile number and transaction details."]
    ]
  },
  responsible: {
    title: "Responsible Gaming",
    subtitle: "Play carefully and within your limits.",
    updated: "11 May 2026",
    sections: [
      ["18+ Only", "Matka Kings is strictly intended for users aged 18 years or above. Underage use is prohibited."],
      ["Play Responsibly", "Users should play only with money they can afford to lose. Do not borrow money or use emergency funds for gaming."],
      ["Set Limits", "Use betting, ank and withdrawal limits responsibly. If gaming causes stress, financial issues or loss of control, stop using the platform and seek help."],
      ["Self Control", "Take breaks, avoid chasing losses, and do not play while emotionally distressed or under the influence of alcohol or substances."],
      ["Restricted Locations", "Users are responsible for ensuring that use of the platform is allowed in their location."],
      ["Support", "If you need account restriction or support, contact us at support@matkakings.com."]
    ]
  },
  contact: {
    title: "Contact Us",
    subtitle: "Support details for users, reviewers and partners.",
    updated: "11 May 2026",
    sections: [
      ["Support Email", "support@matkakings.com"],
      ["Website", "https://matkakings.com"],
      ["Support Hours", "Support is available for account, wallet, payment, OTP and technical issues. Response time may vary depending on request volume."],
      ["Payment Issues", "For deposit or withdrawal issues, include registered mobile number, amount, date, UPI/bank reference and screenshot if available."],
      ["Business Name", "Matka Kings Online Services"]
    ]
  },
  about: {
    title: "About Us",
    subtitle: "About Matka Kings, our platform experience and the digital services we provide.",
    updated: "11 May 2026",
    sections: [
      ["About Matka Kings", "Matka Kings is a mobile-first gaming platform built for users who want fast access to live market information, wallet tools, charts, support and game services in one clean experience."],
      ["Our Platform", "We provide OTP-based login, wallet balance tracking, market rates, game result updates, passbook records, app support and admin-managed services designed for reliable daily play."],
      ["User Experience", "Our focus is simple: a faster interface, clearer game flow, easier account actions and better visibility across phone, tablet and desktop devices."],
      ["Trust & Support", "We work to keep platform rules, transaction updates and support access clear for users. For help, contact support@matkakings.com."]
    ]
  }
};

function LegalPage({ type = "privacy" }) {
  const navigate = useNavigate();
  const page = pages[type] || pages.privacy;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${page.title} - Matka Kings`;
    const description = document.querySelector("meta[name='description']");
    const previousDescription = description?.getAttribute("content") || "";
    description?.setAttribute("content", `${page.title} for Matka Kings online matka play, Aviator, Color Game and account services.`);

    return () => {
      document.title = previousTitle;
      if (description) description.setAttribute("content", previousDescription);
    };
  }, [page.title]);

  return (
    <main style={shell}>
      <header style={topbar}>
        <button type="button" style={backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </button>
        <BrandLogo size="sm" />
      </header>

      <section style={hero}>
        <p style={eyebrow}>{type === "about" ? "Matka Kings Platform" : "Matka Kings Legal"}</p>
        <h1 style={title}>{page.title}</h1>
        <p style={subtitle}>{page.subtitle}</p>
        <span style={updated}>Last updated: {page.updated}</span>
      </section>

      <section style={card}>
        {page.sections.map(([heading, body]) => (
          <article key={heading} style={section}>
            <h2 style={sectionTitle}>{heading}</h2>
            <p style={sectionText}>{body}</p>
          </article>
        ))}
      </section>
    </main>
  );
}

const shell = { minHeight: "100vh", background: "linear-gradient(180deg,#f8fafc,#eef2ff)", color: "#0f172a", padding: "14px 14px 34px", boxSizing: "border-box" };
const topbar = { maxWidth: "920px", margin: "0 auto", minHeight: "56px", display: "flex", alignItems: "center", gap: "12px" };
const backButton = { width: "40px", height: "40px", borderRadius: "14px", border: "1px solid #dbe4f0", background: "#fff", color: "#334155", display: "grid", placeItems: "center" };
const hero = { maxWidth: "920px", margin: "14px auto 0", background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", borderRadius: "28px", padding: "28px 22px", boxShadow: "0 18px 42px rgba(37,99,235,.20)" };
const eyebrow = { margin: 0, opacity: 0.84, fontSize: "12px", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".12em" };
const title = { margin: "8px 0 0", fontSize: "clamp(28px,5vw,44px)", fontWeight: 900, lineHeight: 1.05 };
const subtitle = { margin: "10px 0 0", maxWidth: "680px", color: "#dbeafe", fontSize: "15px", lineHeight: 1.6 };
const updated = { display: "inline-flex", marginTop: "18px", padding: "8px 12px", borderRadius: "999px", background: "rgba(255,255,255,.14)", fontSize: "12px", fontWeight: 800 };
const card = { maxWidth: "920px", margin: "16px auto 0", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "26px", padding: "22px", boxShadow: "0 14px 34px rgba(15,23,42,.08)" };
const section = { padding: "16px 0", borderBottom: "1px solid #edf2f7" };
const sectionTitle = { margin: 0, fontSize: "18px", fontWeight: 900, color: "#0f172a" };
const sectionText = { margin: "8px 0 0", color: "#475569", fontSize: "14px", lineHeight: 1.75 };

export default LegalPage;
